
import { useState, useEffect, useRef, useCallback } from 'react';
import type { Order, CustomerGeneratedImage } from '@/types/order';
import { ORDER_STATUS_CONFIG } from '@/types/order';
import { useFetch } from '@/hooks/useFetch';
import {
  getOrderDetail,
  getOrderImages,
  customerRegenerateImages,
  customerDownloadImages,
  getCustomerRegenStatus,
} from '@/api/orders';
import {
  getRegenerateExpiryDate,
  isRegenerateExpired,
  getDaysUntilExpiry,
  formatExpiryDate,
} from '@/lib/regenerate';
import ImageGrid from './ImageGrid';
import ImageActions from './ImageActions';

interface OrderDetailModalProps {
  order: Order;
  onClose: () => void;
}

const statusStepMap: Record<string, number> = {
  pending: 0,
  paid: 1,
  processing: 2,
  completed: 3,
  cancelled: 0,
};

export default function OrderDetailModal({ order, onClose }: OrderDetailModalProps) {
  const { data: detail, loading } = useFetch(
    () => getOrderDetail(order.orderId),
    [order.orderId],
  );

  const status = ORDER_STATUS_CONFIG[order.status] || ORDER_STATUS_CONFIG.pending;
  const currentStep = statusStepMap[order.status] ?? 0;

  // 재생성/다운로드 기한 계산 (결제일 기준 3개월, 현재는 createDt 사용)
  const paidDt = detail?.createDt || order.createDt;
  const expiryDate = paidDt ? getRegenerateExpiryDate(paidDt) : null;
  const expired = paidDt ? isRegenerateExpired(paidDt) : false;
  const daysLeft = paidDt ? getDaysUntilExpiry(paidDt) : 0;
  const expirySoon = !expired && daysLeft <= 7;

  // ── 이미지 관련 상태 (completed 주문용) ──
  const [images, setImages] = useState<CustomerGeneratedImage[]>([]);
  const [maxRegenCount, setMaxRegenCount] = useState(2);
  const [maxDownloadCount, setMaxDownloadCount] = useState(20);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [selectedIdxs, setSelectedIdxs] = useState<Set<number>>(new Set());
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [regenProgress, setRegenProgress] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 이미지 로드
  const loadImages = useCallback(async () => {
    if (order.status !== 'completed') return;
    setImagesLoading(true);
    try {
      const res = await getOrderImages(order.orderId);
      setImages(res.images);
      setMaxRegenCount(res.maxRegenCount);
      setMaxDownloadCount(res.maxDownloadCount);
    } catch {
      // 이미지 없음 or 에러
    } finally {
      setImagesLoading(false);
    }
  }, [order.orderId, order.status]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  // 폴링 정리
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // 선택 토글
  const handleToggleSelect = (idx: number) => {
    setSelectedIdxs((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  // 전체 선택 토글
  const handleToggleSelectAll = () => {
    if (selectedIdxs.size === images.length) {
      setSelectedIdxs(new Set());
    } else {
      setSelectedIdxs(new Set(images.map((img) => img.generatedImageIdx)));
    }
  };

  // 재생성 실행
  const handleRegenerate = async () => {
    const idxList = Array.from(selectedIdxs);
    setShowConfirmDialog(false);
    setIsRegenerating(true);
    setRegenProgress(0);

    try {
      await customerRegenerateImages(order.orderId, idxList);

      // 폴링 시작
      pollingRef.current = setInterval(async () => {
        try {
          const status = await getCustomerRegenStatus(order.orderId);
          setRegenProgress(status.progress);

          if (status.status === 'completed' || status.status === 'failed') {
            if (pollingRef.current) clearInterval(pollingRef.current);
            pollingRef.current = null;
            setIsRegenerating(false);
            setSelectedIdxs(new Set());
            await loadImages();
          }
        } catch {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          setIsRegenerating(false);
        }
      }, 2000);
    } catch {
      setIsRegenerating(false);
    }
  };

  // 선택 다운로드
  const handleDownloadSelected = async () => {
    const idxList = Array.from(selectedIdxs);
    setIsDownloading(true);
    try {
      await customerDownloadImages(order.orderId, idxList);
    } catch {
      // 에러 처리
    } finally {
      setIsDownloading(false);
    }
  };

  // 전체 다운로드
  const handleDownloadAll = async () => {
    setIsDownloading(true);
    try {
      await customerDownloadImages(order.orderId);
    } catch {
      // 에러 처리
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="sticky top-0 bg-white rounded-t-3xl px-8 py-6 border-b border-gray-100 flex items-center justify-between z-10">
            <div>
              <h2 className="font-serif text-xl font-medium text-[#2D2D2D]">주문 상세</h2>
              <p className="text-sm text-gray-400 mt-0.5">{order.orderId}</p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl text-gray-500"></i>
            </button>
          </div>

          {loading ? (
            <div className="px-8 py-16 flex flex-col items-center gap-4">
              <div className="w-10 h-10 border-2 border-[#C9A96E] border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-gray-400">상세 정보를 불러오는 중...</p>
            </div>
          ) : (
            <div className="px-8 py-6 space-y-8">
              {/* Status Progress */}
              {order.status !== 'cancelled' && (
                <div>
                  <p className="text-xs text-gray-400 font-medium tracking-wide uppercase mb-4">진행 상태</p>
                  <div className="flex items-center gap-0">
                    {['주문 접수', 'AI 작업중', '완료'].map((label, i) => {
                      const stepNum = i + 1;
                      const isActive = currentStep >= stepNum;
                      const isCurrent = currentStep === stepNum;
                      return (
                        <div key={i} className="flex items-center flex-1">
                          <div className="flex flex-col items-center gap-2 flex-1">
                            <div className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-all ${
                              isActive
                                ? isCurrent
                                  ? 'bg-[#C9A96E] text-white shadow-md'
                                  : 'bg-[#2D2D2D] text-white'
                                : 'bg-gray-100 text-gray-400'
                            }`}>
                              {isActive && !isCurrent ? (
                                <i className="ri-check-line text-sm"></i>
                              ) : (
                                stepNum
                              )}
                            </div>
                            <span className={`text-xs font-medium ${isActive ? 'text-[#2D2D2D]' : 'text-gray-400'}`}>
                              {label}
                            </span>
                          </div>
                          {i < 2 && (
                            <div className={`h-0.5 flex-1 mb-5 ${currentStep > stepNum ? 'bg-[#2D2D2D]' : 'bg-gray-200'}`}></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Order Info */}
              <div>
                <p className="text-xs text-gray-400 font-medium tracking-wide uppercase mb-4">주문 정보</p>
                <div className="bg-gray-50 rounded-2xl p-5 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">요금제</span>
                    <span className="font-medium text-[#2D2D2D]">{detail?.productName || order.productName}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">주문일시</span>
                    <span className="font-medium text-[#2D2D2D]">{detail?.createDt || order.createDt}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">업로드 사진</span>
                    <span className="font-medium text-[#2D2D2D]">{detail?.photoCount || order.photoCount}장</span>
                  </div>
                  {(detail?.completedAt || order.completedAt) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">완료일시</span>
                      <span className="font-medium text-green-600">{detail?.completedAt || order.completedAt}</span>
                    </div>
                  )}
                  {order.status === 'processing' && (detail?.estimatedDelivery || order.estimatedDelivery) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">예상 완료</span>
                      <span className="font-medium text-amber-600">{detail?.estimatedDelivery || order.estimatedDelivery}</span>
                    </div>
                  )}
                  <div className="pt-3 border-t border-gray-200 flex justify-between">
                    <span className="text-sm font-medium text-gray-700">결제 금액</span>
                    <span className="text-lg font-bold text-[#C9A96E]">{(detail?.amount || order.amount).toLocaleString()}원</span>
                  </div>
                </div>
              </div>

              {/* Styles & Tones */}
              {detail?.selections && detail.selections.length > 0 && (
                <div>
                  <p className="text-xs text-gray-400 font-medium tracking-wide uppercase mb-4">선택한 스타일</p>
                  <div className="space-y-3">
                    {detail.selections.map((s, i) => (
                      <div key={i} className="flex items-center gap-3 p-4 bg-[#FDF5EA] rounded-xl">
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#C9A96E] text-white text-xs font-bold shrink-0">
                          {i + 1}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-[#2D2D2D]">{s.styleName}</p>
                        </div>
                        <i className="ri-brush-line text-[#C9A96E]"></i>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Completed: 생성 이미지 섹션 ── */}
              {order.status === 'completed' && (
                <div>
                  <p className="text-xs text-gray-400 font-medium tracking-wide uppercase mb-4">
                    생성된 사진
                  </p>

                  {imagesLoading ? (
                    <div className="flex flex-col items-center gap-3 py-8">
                      <div className="w-8 h-8 border-2 border-[#C9A96E] border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-sm text-gray-400">사진을 불러오는 중...</p>
                    </div>
                  ) : images.length === 0 ? (
                    <div className="bg-gray-50 rounded-2xl p-6 text-center">
                      <p className="text-sm text-gray-500">생성된 사진이 없습니다.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* 안내 메시지 */}
                      {expired ? (
                        <div className="bg-red-50 rounded-xl p-4 flex items-start gap-3">
                          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-red-100 shrink-0">
                            <i className="ri-error-warning-line text-red-600 text-sm"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-red-800 mb-0.5">
                              서비스 제공기간이 만료되었어요
                            </p>
                            <p className="text-xs text-red-600 leading-relaxed">
                              결제일로부터 3개월의 서비스 제공기간이 종료되어 재생성 및 다운로드를 이용하실 수 없습니다.
                              {expiryDate && <> (만료일: {formatExpiryDate(expiryDate)})</>}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-emerald-50 rounded-xl p-4 flex items-start gap-3">
                          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-100 shrink-0">
                            <i className="ri-image-line text-emerald-600 text-sm"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-emerald-800 mb-0.5">
                              사진이 완성되었어요!
                            </p>
                            <p className="text-xs text-emerald-600 leading-relaxed">
                              마음에 들지 않는 사진은 선택 후 재생성할 수 있어요 (사진당 최대 {maxRegenCount}회).
                              재생성 시 이전 사진은 복구되지 않으니 신중하게 선택해 주세요.
                              AI가 계속 발전 중이에요! 재생성 1회를 아껴두시면 나중에 더 높은 퀄리티의 사진을 받아보실 수 있어요.
                            </p>
                            {expiryDate && (
                              <p className={`text-xs mt-2 font-medium flex items-center gap-1 ${expirySoon ? 'text-red-600' : 'text-emerald-700'}`}>
                                <i className="ri-calendar-check-line"></i>
                                재생성·다운로드 가능 기한: {formatExpiryDate(expiryDate)}
                                <span className="font-normal">({daysLeft}일 남음)</span>
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* 재생성 진행 바 */}
                      {isRegenerating && (
                        <div className="bg-violet-50 rounded-xl p-4 space-y-2">
                          <div className="flex items-center gap-2">
                            <i className="ri-loader-4-line animate-spin text-violet-600"></i>
                            <p className="text-sm font-medium text-violet-800">
                              AI가 사진을 재생성하고 있어요...
                            </p>
                          </div>
                          <div className="w-full bg-violet-200 rounded-full h-2">
                            <div
                              className="bg-violet-600 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${regenProgress}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-violet-500 text-right">{regenProgress}%</p>
                        </div>
                      )}

                      {/* 액션 버튼 */}
                      <ImageActions
                        images={images}
                        selectedIdxs={selectedIdxs}
                        maxDownloadCount={maxDownloadCount}
                        onToggleSelectAll={handleToggleSelectAll}
                        onRegenerate={() => setShowConfirmDialog(true)}
                        onDownloadSelected={handleDownloadSelected}
                        onDownloadAll={handleDownloadAll}
                        isRegenerating={isRegenerating}
                        isDownloading={isDownloading}
                        isExpired={expired}
                      />

                      {/* 이미지 그리드 */}
                      <ImageGrid
                        images={images}
                        selectedIdxs={selectedIdxs}
                        onToggleSelect={handleToggleSelect}
                        isRegenerating={isRegenerating}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* In Progress Message */}
              {order.status === 'processing' && (
                <div className="bg-blue-50 rounded-2xl p-5 flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 shrink-0">
                    <i className="ri-magic-line text-blue-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-800 mb-1">AI가 열심히 작업 중이에요!</p>
                    <p className="text-xs text-blue-600 leading-relaxed">
                      선택하신 스타일로 웨딩 사진을 생성하고 있습니다.
                      {(detail?.estimatedDelivery || order.estimatedDelivery) && (
                        <>예상 완료일 <strong>{detail?.estimatedDelivery || order.estimatedDelivery}</strong>까지 완성된 결과물을 받아보실 수 있어요.</>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Pending - 결제 대기 */}
              {order.status === 'pending' && (
                <div className="bg-amber-50 rounded-2xl p-5 flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 shrink-0">
                    <i className="ri-error-warning-line text-amber-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-800 mb-1">결제가 완료되지 않았어요</p>
                    <p className="text-xs text-amber-600 leading-relaxed">
                      결제를 완료해야 주문이 접수됩니다. 결제를 진행해 주세요.
                    </p>
                  </div>
                </div>
              )}

              {/* Paid - 주문 접수 완료 */}
              {order.status === 'paid' && (
                <div className="bg-amber-50 rounded-2xl p-5 flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 shrink-0">
                    <i className="ri-time-line text-amber-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-800 mb-1">주문이 접수되었어요</p>
                    <p className="text-xs text-amber-600 leading-relaxed">
                      곧 AI 작업이 시작될 예정입니다. 작업이 시작되면 알려드릴게요.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 재생성 확인 다이얼로그 */}
      {showConfirmDialog && (
        <div
          className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
          onClick={() => setShowConfirmDialog(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl space-y-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-100 shrink-0">
                <i className="ri-error-warning-line text-amber-600 text-lg"></i>
              </div>
              <h3 className="text-base font-medium text-[#2D2D2D]">재생성 확인</h3>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              선택한 <strong>{selectedIdxs.size}장</strong>의 사진을 재생성합니다.
              <br />
              <span className="text-red-500 font-medium">재생성 시 이전 사진은 복구되지 않습니다.</span>
            </p>
            {/Mobi|Android|iPhone/i.test(navigator.userAgent) && (
              <div className="flex items-start gap-2.5 bg-blue-50 rounded-xl p-3.5">
                <i className="ri-computer-line text-blue-500 text-lg shrink-0 mt-0.5"></i>
                <p className="text-xs text-blue-700 leading-relaxed">
                  재생성은 시간이 다소 걸릴 수 있습니다.
                  <br />
                  <strong className="font-medium">PC 환경에서 진행하시는 것을 권장드립니다.</strong>
                  <br />
                  <span className="text-blue-500">모바일에서는 화면이 꺼지면 작업이 중단될 수 있습니다.</span>
                </p>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
              >
                취소
              </button>
              <button
                onClick={handleRegenerate}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-[#C9A96E] hover:bg-[#B8984E] transition-colors cursor-pointer"
              >
                그래도 재생성하기
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
